import React from 'react'

import { Box, Text } from '@mantine/core'
import { useSessionStorage } from '@mantine/hooks'

import { PW_DEBUG_ENABLED } from '../../../components/MantineInputField/PwDebug/utils'
import {
  SCREENSHOT_SUPPORT_ENABLED,
  TestIdScreenshotButton,
} from '../../../components/TestIdScreenshotButton'
import { DEFAULT_TABLE_PROPS } from '../constants'
import FilterControlbar from '../FilterControlBar'
import PaginatedTable from '../PaginatedTable'
import PaginationBar from '../PaginationBar'
import { DefaultImplementationProps } from './default-paginated-table-implementation'
import { use1080pOrLess } from './use1080pOrLess'
import useDefaultImplementationStyles from '../styles/useDefaultImplementationStyles'

const DATA_TEST_ID_PAGINATED_TABLE = 'paginated-table'
const DefaultPaginatedTableImplementation = <T,>({
  wrappingBoxProps = {},
  paginationState,
  tableInstance,
  paginatedQuery,
  sortFilter,
  filterDefinitions = [],
  fulltextFilterInputProps,
  tableProps = DEFAULT_TABLE_PROPS,
  selectionState,
  allIdsOnActivePage,
  selectionActions: SelectionActions,
  withPageExport = true,
}: DefaultImplementationProps<T>): React.ReactElement => {
  const { classes, cx } = useDefaultImplementationStyles()

  const is1080pOrLess = use1080pOrLess()

  const [pwDebugEnabled] = useSessionStorage<boolean>({
    key: PW_DEBUG_ENABLED,
  })

  const [screenshotSupportEnabled] = useSessionStorage<boolean>({
    key: SCREENSHOT_SUPPORT_ENABLED,
  })

  return (
    <Box
      className={cx(classes.wrappingBox, wrappingBoxProps.className)}
      data-testid={DATA_TEST_ID_PAGINATED_TABLE}
      sx={() => ({
        border: pwDebugEnabled ? '1px solid red' : 'none',
      })}
      {...wrappingBoxProps}
    >
      {false && (
        <Box
          style={{
            position: 'absolute',
            top: 60,
            right: 10,
            zIndex: 1000,
          }}
        >
          <TestIdScreenshotButton
            targetTestId={DATA_TEST_ID_PAGINATED_TABLE}
            filename={`${DATA_TEST_ID_PAGINATED_TABLE}-${window.location.pathname}`}
          />
        </Box>
      )}
      {pwDebugEnabled && (
        <Text>data-testid="default-paginated-table-implementation"</Text>
      )}
      <div>
        <FilterControlbar
          sortFilter={sortFilter}
          filterDefinitions={filterDefinitions}
          fulltextFilterTextInputProps={{
            size: is1080pOrLess ? 'xs' : 'sm',
            ...fulltextFilterInputProps,
          }}
          withPageExport={withPageExport}
          tableInstance={tableInstance}
          actionIconSize={is1080pOrLess ? 'md' : 'lg'}
        />
      </div>

      <div
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <PaginatedTable<T>
          tableInstance={tableInstance}
          paginatedQuery={paginatedQuery}
          paginationState={paginationState}
          sortFilter={sortFilter}
          tableProps={tableProps}
          fulltextFilterInputProps={fulltextFilterInputProps}
          selectionState={selectionState}
          allIdsOnActivePage={allIdsOnActivePage}
          selectionActions={SelectionActions}
        />
      </div>
      <div>
        <PaginationBar
          size={is1080pOrLess ? 'sm' : 'md'}
          perPageSelectInputProps={{
            size: 'xs',
          }}
          isError={paginatedQuery.isError}
          isLoading={paginatedQuery.isFetching}
          {...paginationState}
        />
      </div>
    </Box>
  )
}

export default DefaultPaginatedTableImplementation
